import { Tooltip, Typography } from '@mui/material';
import {ListTableCell} from '../components/CustomStyles/StyledComponents';
import {agcColours} from './colourHelper'
import { UserAccessLevel } from './permissionsHelper';

export const ResultStatus = Object.freeze({
    NotCalculated: 0,
    Completed: 1,
    Error: 2,
    isCompleted : (status)=>{ return status === ResultStatus.Completed }

});

export const ReportStatus = Object.freeze({
    ReportInitiated: 0,
    DataChecksErrors: 1,
    DataChecksPassed: 2,
    CalculationInProgress: 3,
    CalculationCompleted: 4,
    ReviewRequested: 5,
    ReviewInProgress: 6,
    ReviewCompletedAndValid: 7,
    ReviewCompletedAndInvalid: 8,
    CalculationErrorOccured: 9,
    Finalised: 10,
    isCalculated: (status) => { return (status === ReportStatus.CalculationCompleted) },
    isPreCalculation: (status) => { return (status < ReportStatus.CalculationCompleted) },
    isUnderReview: (status) => { return ([ReportStatus.ReviewRequested, ReportStatus.ReviewInProgress].includes(status)) },
    isValidated: (status) => { return ([ReportStatus.ReviewCompletedAndValid, ReportStatus.ReviewCompletedAndInvalid].includes(status)) },
    isFinalised: (status) => { return (status === ReportStatus.Finalised) },
    isClosed: (status) => { return ReportStatus.isFinalised(status) || ReportStatus.isValidated(status) },
    canSubmit: (status) => { return ([ReportStatus.CalculationCompleted, ReportStatus.DataChecksErrors].includes(status)) },
    isReviewRequest:(status) => { return (status === ReportStatus.ReviewRequested) },
    isReviewOrFinaliseRequest:(status) => { return (status === ReportStatus.ReviewRequested || status === ReportStatus.Finalised ) },
    canDelete:  (status) => { return (status !== ReportStatus.ReviewCompletedAndValid && status !== ReportStatus.Finalised) },

    canEdit: (reptStatus, accessLvl) => {
        let rptStatusOK = ![ReportStatus.ReviewRequested, ReportStatus.ReviewInProgress, 
            ReportStatus.ReviewCompletedAndValid , ReportStatus.ReviewCompletedAndInvalid , 
            ReportStatus.Finalised].includes(reptStatus)
        let userCanEdit  = UserAccessLevel.canEdit(accessLvl)
        return (userCanEdit && rptStatusOK) 
    },

    getDeleteTooltip: (status) => { 
        return ReportStatus.canDelete(status)? "": `${ReportStatus.getStatusText(status)} reports cannot be deleted.`
    },

    getColor: (status) =>{
        const statusInfo=ReportStatusDisplay.get(status)
        return statusInfo?statusInfo.c:agcColours.PrussianBlue;
    },

    getColorTableCell: (status) => {
        const statusInfo=ReportStatusDisplay.get(status)
        if(statusInfo){
            return <Tooltip title={statusInfo.tt}><ListTableCell color={statusInfo.c}>{statusInfo.n}</ListTableCell></Tooltip>
        }
        return <ListTableCell></ListTableCell>
    },
    getStatusText: (status) => {
        const statusInfo = ReportStatusDisplay.get(status)
        return statusInfo? statusInfo.n : "Unknown" 
    },
    getStatusTooltip: (status) => {
        const statusInfo=ReportStatusDisplay.get(status)
        return statusInfo? statusInfo.tt : "Unknown" 
    },
    getTooltipList: () =>{
        let allValues = [...ReportStatusDisplay.entries()];
        let values = allValues.filter((v)=> ![ReportStatus.DataChecksPassed,ReportStatus.CalculationInProgress].includes(v[0]))
        return values.map( (e) => {return {id:e[0], n:e[1].n, t:e[1].tt} })
    },
    renderStatusTooltip: () => {
        return ReportStatus.getTooltipList().map((e) =>
          <Typography key={e.id} variant="caption" component="p">
            <strong>{e.n}</strong>: {e.t}
          </Typography>)
    }
});

export const ReportStatusDisplay = new Map([
    [ReportStatus.ReportInitiated, {n:'Blank', c:agcColours.PrussianBlue,tt:'This report was created but no data was entered.'}],
    [ReportStatus.DataChecksErrors, {n: 'Missing Data', c:agcColours.MilanoRed, tt:'This report has failed data checks which usually means data is missing.'}],
    [ReportStatus.DataChecksPassed, {n: 'Incomplete', c:' #C94D49', tt:'This report has not been submitted or finalised.'}],
    [ReportStatus.CalculationInProgress, {n: 'Incomplete', c:' #C94D49',tt:'This report has not been submitted for validation or finalised.'}],
    [ReportStatus.CalculationCompleted, {n: 'Incomplete', c:' #C94D49',tt:'This report has not been submitted for validation or finalised.'}],
    [ReportStatus.ReviewRequested, {n: 'Pending Validation', c:agcColours.AgrecalcButtercup, tt:'This report has been submitted for validation  and is awaiting review.'}],
    [ReportStatus.ReviewInProgress, {n: 'Under Validation', c:agcColours.AgrecalcButtercup, tt:'This report has been submitted for validation and is currently being reviewed.'}],
    [ReportStatus.ReviewCompletedAndValid, {n: 'Validated', c:' #5ace5d', tt:' This report has been completed and accepted to the benchmarking database.'}],
    [ReportStatus.ReviewCompletedAndInvalid, {n: 'Valid (not in benchmarks)', c:' #595959', tt:'This report has been completed and not accepted to the benchmarking database.'}],
    [ReportStatus.CalculationErrorOccured, {n: 'Invalid Data', c:agcColours.MilanoRed, tt:"This report did not calculate which usually means there's invalid or inconsistent data."}],
    [ReportStatus.Finalised, {n: 'Finalised (not validated)', c:' #5ace5d', tt:"This report has been deemed finished by the user. It's not validated."}]])


export const showNonValidatedReportText = (status) => {
    return (status !== ReportStatus.ReviewCompletedAndValid
        && status !== ReportStatus.ReviewCompletedAndInvalid);
}

export const ReportHlp = Object.freeze({
    getYearFromStr: (str) => { return (new Date(str)).getUTCFullYear(); },

    findSameYearAlreadyValidated: (reportList, reportID) => {
        let rept = reportList?.find((r) => (r.reportId === reportID));
        if (rept) {
            let reptYearEnd = ReportHlp.getYearFromStr(rept.yearEnd);
            let match = reportList?.find((r) => (
                ReportHlp.getYearFromStr(r.yearEnd) === reptYearEnd
                && r.reportId !== rept.reportID
                && ReportStatus.isValidated(r.status)));
            return match;
        }
        return null;
    }
});

// hack to exclude Hedges from results until it's corrected in the backend
export const resultsDataMap = (rowData, noFooter, mapFn) => {
    const titleMatch = (row) => { return row.itemTitle?.match(/Hedges \([<>]30yrs\)/) == null };
    if (rowData) {
        let entries = Object.entries(rowData.filter(titleMatch))
        return entries.slice(0, entries.length - (noFooter ? 0 : 1)).map(mapFn)
    }
}

// create percentual data from the table data
export const table2ChartData = (tableData) => {
    let chartData = { emissionSource: [] };
    if (!tableData?.quickGlanceEmissions) {
        return chartData;
    }
    let itemsOnly = tableData.quickGlanceEmissions.filter((e) => e.title !== 'Total emissions***')
    let total = itemsOnly.reduce((acc, c) => acc + c.emission, 0)
    chartData.emissionSource = itemsOnly.map((e) => { return { title: e.title, value: (e.emission / total * 100) } })
    // sort descending
    chartData.emissionSource.sort((a, b) => b.value - a.value)

    return chartData;

}